import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';

import { Subject } from 'rxjs';

import {
    PcvizDBSelectionController,
    PcvizFeature,
    PcvizInformationTool,
    PcvizLayer,
    PcvizPointVisibilityVisitor,
    PcvizProject,
    PcvizSceneView,
    PcvizSelectionObject,
    PcvizUpdateVisibilityVisitor,
    PcvizVectorLayer,
} from '@mirage-projects/cw-models';
import { DbPcvizService, GisPcvizService, UtilsPcvizService } from '@mirage-projects/cw-pcviz-core';

import { EXTERNAL_COMMUNICATION_EVENTS, ExternalCommunicationService } from '../external-communication';

interface PostMessageOptions extends WindowPostMessageOptions {
    message: PostMessageOptionsMessage;
}

interface PostMessageOptionsMessage {
    type: string;
    message: {
        layer: PcvizLayer;
    };
}

/**
 * This service communicates the embedded app with its parent
 */
@Injectable({ providedIn: 'root' })
export class IframeCommunicationService implements OnDestroy {
    private updateLayerStylesSubject$ = new Subject<void>();

    private aptixUrl = '';
    private view3d!: PcvizSceneView;
    private infoTool!: PcvizInformationTool;

    private selectionCtrl!: PcvizDBSelectionController;
    private selectedLayer: PcvizLayer | undefined;
    private previousLayer: PcvizLayer | undefined;
    private pointVisibilityVisitor!: PcvizPointVisibilityVisitor;
    private updateVisibilityVisitor!: PcvizUpdateVisibilityVisitor;
    private pcvizProject!: PcvizProject;

    constructor(
        private externalCommService: ExternalCommunicationService,
        private gisPcvizService: GisPcvizService,
        private dbPcvizService: DbPcvizService,
        private utilsPcvizService: UtilsPcvizService,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.initVisibilityVisitors();
    }

    /**
     * Initializes iFrame communication
     * @param pcvizProject Current project
     * @param view3d Current view 3d scene
     */
    init(pcvizProject: PcvizProject, view3d: PcvizSceneView): void {
        this.aptixUrl = this.getUrlFormatted();

        if (!this.aptixUrl) {
            return;
        }

        this.registerListeners();

        this.pcvizProject = pcvizProject;
        this.view3d = view3d;
        this.infoTool = this.view3d.getTool('information') as PcvizInformationTool;
    }

    updateLayerStyles(): void {
        this.updateLayerStylesSubject$.next();
    }

    ngOnDestroy(): void {
        this.updateLayerStylesSubject$.unsubscribe();
    }

    private registerListeners(): void {
        this.registerMessageListener();
        this.registerSelectionObjectListener();
        this.registerUpdatePropertiesListener();
    }

    /**
     * this method connects to aptix to receive the id and the type of the layer selected
     */
    private registerMessageListener(): void {
        this.externalCommService.onEvents(EXTERNAL_COMMUNICATION_EVENTS.IFRAME_MESSAGE).subscribe((event) => this.messageReceived(event));
    }

    private registerSelectionObjectListener(): void {
        this.selectionCtrl = this.dbPcvizService.getControllerDB('SelectionObject');
        this.selectionCtrl.registerListener(
            this.dbPcvizService.getListenerController().PROJECT,
            (selectionObject: PcvizSelectionObject) => {
                this.handlerSelection(selectionObject);
            }
        );
    }

    private registerUpdatePropertiesListener(): void {
        this.updateLayerStylesSubject$.subscribe(() => {
            const selectionObject = this.selectionCtrl.getSelectionObject();

            if (!selectionObject) {
                return;
            }

            this.handlerSelection(selectionObject, true);
        });
    }

    private handlerSelection(selectionObject: PcvizSelectionObject, isFromUpdate = false): void {
        const layer = selectionObject.getLayer();

        if (!layer || layer.getType() === 'LayerGroup') {
            return;
        }

        this.sendMessage(layer, isFromUpdate);
    }

    /**
     * send message to Aptix
     * @param layer layer selected
     */
    private sendMessage(layer: PcvizLayer, isFromUpdate = false): void {
        this.selectedLayer = layer;

        if (this.isLayerAlreadySelected() && !isFromUpdate) {
            return;
        }

        if (this.previousLayer?.getType() === 'PointCloud') {
            this.restorePreviousLayerVisibility();
        }

        this.setPreviousLayer();

        const message = {
            type: 'select',
            message: {
                layer: this.selectedLayer._layerObject,
                success: true,
            },
        };

        this.externalCommService.sendEvent(EXTERNAL_COMMUNICATION_EVENTS.IFRAME_MESSAGE, message, this.aptixUrl);
    }

    private getUrlFormatted(): string {
        let url = this.document.referrer;

        if (url.endsWith('/')) {
            url = url.slice(0, url.length - 1);
        }

        return url;
    }

    private setPreviousLayer(): void {
        this.previousLayer = this.selectedLayer;
    }

    private updateLayerSelected(): void {
        if (!this.selectedLayer) {
            this.sendError();
            return;
        }

        this.setPreviousLayer();
        this.updateLayerByType();
    }

    /**
     * only supports vector and pointcloud layers
     */
    private updateLayerByType(): void {
        const layerType = this.selectedLayer?.getType();

        if (layerType === this.gisPcvizService.getLayerType().VECTOR) {
            this.selectVectorLayer();
        } else if (layerType === this.gisPcvizService.getLayerType().POINTCLOUD) {
            this.selectPointcloudLayer();
        }
    }

    private async selectVectorLayer(): Promise<void> {
        if (!this.selectedLayer) {
            return;
        }

        try {
            const { features } = await (this.selectedLayer as PcvizVectorLayer).getAllFeaturesByPages();
            if (features?.length === 1) {
                this.highlightLayerFeature(features[0]);
            } else {
                this.setSelectionObject(null);
            }

            this.zoomToLayer(this.selectedLayer);
        } catch {
            this.sendError();
        }
    }

    private selectPointcloudLayer(): void {
        if (!this.selectedLayer) {
            return;
        }

        this.changePointCloudHighlight(true, this.selectedLayer);
        this.setSelectionObject(null);
        this.zoomToLayer(this.selectedLayer);
    }

    private sendError(): void {
        const message = {
            type: 'error',
            message: {
                description: 'There was an error getting the layer',
                success: false,
            },
        };

        this.externalCommService.sendEvent(EXTERNAL_COMMUNICATION_EVENTS.IFRAME_MESSAGE, message, this.aptixUrl);
    }

    private isLayerAlreadySelected(): boolean {
        return !!(this.previousLayer && this.selectedLayer && this.previousLayer === this.selectedLayer);
    }

    /**
     * Sets the layer selected and its feature, if apply, to selection controller
     * @param feature the feature of the layer selected
     */
    private setSelectionObject(feature: PcvizFeature | null): void {
        if (!this.selectedLayer) {
            return;
        }

        const selectionObject = this.selectionCtrl.createSelectionObject(this.selectedLayer.getType());
        selectionObject.setLayer(this.selectedLayer);

        if (feature) {
            selectionObject.setFeature(feature);
        }

        this.selectionCtrl.setSelectionObject(selectionObject);
    }

    private messageReceived(event: PostMessageOptions): void {
        if (event.targetOrigin !== this.aptixUrl || !event.message) {
            return;
        }

        this.handlerContentReceived(event.message);
    }

    /**
     * method to change the highlighting of a pointcloud
     * @param isSelected indicates if the pointcloud has to be highlighted or not
     * @param layer layer to apply the highlight
     */
    private changePointCloudHighlight(isSelected: boolean, layer: PcvizLayer): void {
        const node = this.view3d.getLayerRenderer().getNodeFromLayer(layer);

        if (!node) {
            return;
        }

        const option = isSelected ? this.updateVisibilityVisitor : this.pointVisibilityVisitor;
        node.accept(option);
    }

    /**
     * Highlights the feature of the Vector Layer selected
     * @param feature the feature of the layer selected
     */
    private async highlightLayerFeature(feature: any): Promise<void> {
        if (!this.selectedLayer) {
            return;
        }

        try {
            const featureDetail: PcvizFeature = await (this.selectedLayer as PcvizVectorLayer).getFeature(feature.id);
            this.setSelectionObject(featureDetail);
        } catch {
            this.sendError();
        }
    }

    private handlerContentReceived(data: PostMessageOptionsMessage): void {
        const { message, type } = data;

        // supports only a layer selection
        if (type !== 'select' || !message.layer || message.layer.type !== 'layers') {
            return;
        }

        this.setLayerById(message.layer.id);

        if (this.isLayerAlreadySelected()) {
            return;
        }

        if (this.previousLayer) {
            this.clearInformationToolSelection();

            if (this.previousLayer.getType() === 'PointCloud') {
                this.restorePreviousLayerVisibility();
            }
        }

        this.updateLayerSelected();
    }

    private clearInformationToolSelection(): void {
        if (!this.infoTool) {
            this.infoTool = this.view3d.getTool('information') as PcvizInformationTool;
        }

        if (this.infoTool.getFeatureSelected()) {
            this.infoTool.clearTool();
        }
    }

    private setLayerById(id: number): void {
        this.selectedLayer = this.pcvizProject.getLayerById(id);
    }

    /**
     * Restore the visibility of the previous layer selected when the previousLayer is a Pointcloud Layer.
     * @returns void
     */
    private restorePreviousLayerVisibility(): void {
        if (this.previousLayer) {
            this.changePointCloudHighlight(false, this.previousLayer);
        }
    }

    /**
     * Zooms the viewer to the current selected layer
     */
    private zoomToLayer(layer: PcvizLayer | undefined): void {
        if (!layer) {
            return;
        }

        const viewerController = this.dbPcvizService.getControllerDB('View');
        viewerController.zoomToLayer(layer, {});
    }

    private initVisibilityVisitors(): void {
        this.updateVisibilityVisitor = this.utilsPcvizService.getUpdateVisibilityVisitorInstance();
        this.updateVisibilityVisitor.setTemporalInsiderList([
            this.utilsPcvizService.getInsider2DInstance(
                undefined,
                undefined,
                undefined,
                undefined,
                this.utilsPcvizService.getUtils().INSIDER_TYPES.HIGHLIGHT
            ),
        ]);
        this.pointVisibilityVisitor = this.utilsPcvizService.getPointVisibilityVisitorInstance();
        this.pointVisibilityVisitor.setMode(this.utilsPcvizService.getUtils().POINT_MODES.SELECTED);
    }
}
